import useSWRMutation from 'swr/mutation';
import {
  type PostClaimInviteRequest,
  postClaimInviteRequest,
} from '@core-api/workspace-locator/workspace-locator/workspaces/{slug}/invite-link/claim-invite';
import { useRouter } from '@mentimeter/next-navigation';
import { captureException } from '@mentimeter/errors/sentry';
import { getWorkspaceRegion } from '@mentimeter/http-clients';
import { InviteError } from '../../errors/InviteError';
import type { JoinErrorEnum } from './handleErrorMessage';

type ClaimInviteArguments = Omit<PostClaimInviteRequest, 'region' | 'slug'>;

interface UseClaimInviteOptions {
  onSuccess?: () => void;
  onError?: (error: InviteError) => void;
}

export function useClaimInviteLink(
  slug: string,
  options?: UseClaimInviteOptions,
) {
  const router = useRouter();
  return useSWRMutation(
    '/invite-link/claim-invite',
    async (_key: string, { arg: args }: { arg: ClaimInviteArguments }) => {
      const region = await getWorkspaceRegion(slug);
      const { email } = args;
      const request = postClaimInviteRequest({
        region,
        email,
        slug,
      });

      const response = await fetch(request);

      if (!response.ok) {
        if (response.status === 409) {
          router.push('/app');
          return;
        }
        const body = (await response.json().catch(() => undefined)) as
          | { code?: JoinErrorEnum }
          | undefined;
        throw new InviteError(body?.code);
      }
      return;
    },
    {
      onSuccess: () => {
        options?.onSuccess?.();
      },
      onError: (e) => {
        let error = e;
        if (!(error instanceof InviteError)) {
          captureException(error);
          error = new InviteError(undefined);
        }

        options?.onError?.(error);
      },
    },
  );
}
